import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { useCategories } from '../../hooks/useApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import NotificationBell from '../../components/common/NotificationBell';
import UserAvatar from '../../components/common/UserAvatar';
import CustomerBottomNav from '../../components/common/CustomerBottomNav';
import AccountStatusBanner from '../../components/common/AccountStatusBanner';
import BannerSlider from '../../components/common/BannerSlider';
import { 
  Search, MapPin, User, Calendar, Wallet, ChevronLeft, ChevronRight as ChevronRightIcon,
  Droplets, Zap, Wind, Sparkles, Hammer, Tv, PaintBucket, Wrench,
  ChevronRight, Star, Heart, Car, Bug, Leaf, Package, Monitor, Flame, Shield,
  Scissors, Truck, Smartphone, Home as HomeIcon, Brush, Settings, HardDrive,
  Paintbrush, Cog, Key, Lock, Fan, Drill, Thermometer, Gauge, TreeDeciduous, Flower2,
  Sprout, Sun, CloudRain, Umbrella, Snowflake, Building, Building2, Warehouse, Laptop,
  Wifi, Printer, Camera, Speaker, Headphones, Utensils, Baby, Dog, Cat, Dumbbell,
  Stethoscope, Briefcase, Sofa, Bed, Bath, Shirt, Coffee, Wine, Pizza, Gift, Crown,
  Gem, ShoppingBag, Watch, Glasses, GraduationCap, Music, Book
} from 'lucide-react';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Comprehensive icon map matching all admin icons
const iconMap = {
  // Home Services - Primary
  'Droplets': Droplets,
  'Zap': Zap,
  'Wind': Wind,
  'Sparkles': Sparkles,
  'Hammer': Hammer,
  'Tv': Tv,
  'PaintBucket': PaintBucket,
  'Wrench': Wrench,
  'Paintbrush': Paintbrush,
  'Bug': Bug,
  'Leaf': Leaf,
  'Truck': Truck,
  'Home': HomeIcon,
  
  // Tools & Repairs
  'Drill': Drill,
  'Cog': Cog,
  'Settings': Settings,
  'Key': Key,
  'Lock': Lock,
  'Fan': Fan,
  'Thermometer': Thermometer,
  'Gauge': Gauge,
  'Brush': Brush,
  
  // Building & Property
  'Building': Building,
  'Building2': Building2,
  'Warehouse': Warehouse,
  
  // Nature & Garden
  'TreeDeciduous': TreeDeciduous,
  'Flower2': Flower2,
  'Sprout': Sprout,
  'Sun': Sun,
  'CloudRain': CloudRain,
  'Umbrella': Umbrella,
  'Snowflake': Snowflake,
  'Flame': Flame,
  
  // Tech & Electronics
  'Monitor': Monitor,
  'Laptop': Laptop,
  'Smartphone': Smartphone,
  'Wifi': Wifi,
  'Printer': Printer,
  'Camera': Camera,
  'Speaker': Speaker,
  'Headphones': Headphones,
  'HardDrive': HardDrive,
  
  // Vehicles & Transport
  'Car': Car,
  'Package': Package,
  
  // Lifestyle & Personal
  'Heart': Heart,
  'Scissors': Scissors,
  'Utensils': Utensils,
  'Baby': Baby,
  'Dog': Dog,
  'Cat': Cat,
  'Dumbbell': Dumbbell,
  'Stethoscope': Stethoscope,
  'Briefcase': Briefcase,
  'Shield': Shield,
  
  // Furniture & Interior
  'Sofa': Sofa,
  'Bed': Bed,
  'Bath': Bath,
  'Shirt': Shirt,
  
  // Other Services
  'Coffee': Coffee,
  'Wine': Wine,
  'Pizza': Pizza,
  'Gift': Gift,
  'Crown': Crown,
  'Gem': Gem,
  'ShoppingBag': ShoppingBag,
  'Star': Star,
  'Watch': Watch,
  'Glasses': Glasses,
  'GraduationCap': GraduationCap,
  'Music': Music,
  'Book': Book,
};

// Color name to Tailwind class mapping (matches admin panel)
const colorNameToClass = {
  blue: 'bg-blue-100 text-blue-600',
  sky: 'bg-sky-100 text-sky-600',
  cyan: 'bg-cyan-100 text-cyan-600',
  teal: 'bg-teal-100 text-teal-600',
  indigo: 'bg-indigo-100 text-indigo-600',
  emerald: 'bg-emerald-100 text-emerald-600',
  green: 'bg-green-100 text-green-600',
  lime: 'bg-lime-100 text-lime-600',
  yellow: 'bg-yellow-100 text-yellow-600',
  amber: 'bg-amber-100 text-amber-600',
  orange: 'bg-orange-100 text-orange-600',
  red: 'bg-red-100 text-red-600',
  rose: 'bg-rose-100 text-rose-600',
  pink: 'bg-pink-100 text-pink-600',
  fuchsia: 'bg-fuchsia-100 text-fuchsia-600',
  purple: 'bg-purple-100 text-purple-600',
  violet: 'bg-violet-100 text-violet-600',
  slate: 'bg-slate-100 text-slate-600',
  gray: 'bg-gray-100 text-gray-600',
  stone: 'bg-stone-100 text-stone-600',
  zinc: 'bg-zinc-100 text-zinc-600',
};

// Color map for categories - used as fallback when icon_color is not set
const colorMap = {
  'Plumbing': 'bg-blue-100 text-blue-600',
  'Electrical': 'bg-yellow-100 text-yellow-600',
  'Aircon': 'bg-cyan-100 text-cyan-600',
  'Cleaning': 'bg-pink-100 text-pink-600',
  'Carpentry': 'bg-amber-100 text-amber-600',
  'Appliance Repair': 'bg-purple-100 text-purple-600',
  'Painting': 'bg-emerald-100 text-emerald-600',
  'General Handyman': 'bg-slate-100 text-slate-600',
};

// Dynamic color classes based on icon_color field
const iconColorClasses = {
  'blue': 'bg-blue-100 text-blue-600',
  'cyan': 'bg-cyan-100 text-cyan-600',
  'teal': 'bg-teal-100 text-teal-600',
  'emerald': 'bg-emerald-100 text-emerald-600',
  'lime': 'bg-lime-100 text-lime-600',
  'yellow': 'bg-yellow-100 text-yellow-600',
  'amber': 'bg-amber-100 text-amber-600',
  'orange': 'bg-orange-100 text-orange-600',
  'red': 'bg-red-100 text-red-600',
  'pink': 'bg-pink-100 text-pink-600',
  'purple': 'bg-purple-100 text-purple-600',
  'violet': 'bg-violet-100 text-violet-600',
  'indigo': 'bg-indigo-100 text-indigo-600',
  'fuchsia': 'bg-fuchsia-100 text-fuchsia-600',
  'rose': 'bg-rose-100 text-rose-600',
  'slate': 'bg-slate-100 text-slate-600',
  'gray': 'bg-gray-100 text-gray-600',
  'green': 'bg-green-100 text-green-600',
  'sky': 'bg-sky-100 text-sky-600',
};

export default function CustomerHome() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { categories, isLoading } = useCategories();
  const [topProviders, setTopProviders] = useState([]);
  const [promos, setPromos] = useState([]);
  const [currentPromoIndex, setCurrentPromoIndex] = useState(0);
  const [hasAddress, setHasAddress] = useState(true); // Assume true initially
  const promoSliderRef = useRef(null);
  
  useEffect(() => {
    // Fetch top-rated providers
    axios.get(`${API}/providers/top-rated?limit=5`)
      .then(res => setTopProviders(res.data || []))
      .catch(() => {});
    
    // Fetch active promo codes
    axios.get(`${API}/promo-codes/active`)
      .then(res => setPromos(res.data || []))
      .catch(() => {});
    
    // Check if user has saved addresses
    const token = localStorage.getItem('token');
    if (token) {
      axios.get(`${API}/addresses`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => setHasAddress(res.data && res.data.length > 0))
        .catch(() => setHasAddress(true)); // On error, don't show alert
    }
  }, []);

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-slate-900 pt-4 pb-8 px-4 rounded-b-3xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-slate-400 text-sm">Magandang araw,</p>
            <h1 className="text-white font-bold text-xl font-[Poppins]">{user?.name || 'Customer'}</h1>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <button 
              className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden"
              onClick={() => navigate('/profile')}
              data-testid="profile-btn"
            >
              <UserAvatar 
                src={user?.profile_image} 
                name={user?.name} 
                role="customer"
                size="md"
              />
            </button>
          </div>
        </div>
        
        {/* Search Bar */}
        <div className="relative" onClick={() => navigate('/service-search')} style={{ cursor: 'pointer' }}>
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input 
            placeholder="Search for services..." 
            className="h-12 pl-12 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 rounded-xl pointer-events-none"
            data-testid="search-input"
            readOnly
          />
        </div>
        
        {/* Location */}
        <div className="flex items-center gap-2 mt-4 text-slate-400">
          <MapPin className="w-4 h-4" />
          <span className="text-sm">Metro Manila, Philippines</span>
        </div>
      </header>

      {/* Main Content */}
      <main className="px-4 -mt-4">
        {/* Address Alert */}
        {!hasAddress && (
          <div 
            className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4 cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => navigate('/profile')}
            data-testid="address-alert"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-amber-700" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-amber-900">Set Your Address</p>
                <p className="text-sm text-amber-700 mt-0.5">
                  Add a service address to help providers reach you. Tap here to add your location.
                </p>
              </div>
              <ChevronRight className="w-5 h-5 text-amber-600 flex-shrink-0 mt-2" />
            </div>
          </div>
        )}

        {/* Promotional Banner Slider */}
        <div className="mb-4">
          <BannerSlider audience="customer" />
        </div>
        
        {/* Account Status Warnings (Identity Verification) */}
        <AccountStatusBanner />
        
        {/* Promo Banner Slider */}
        {promos.length > 0 && (
          <div className="relative mb-6" data-testid="promo-banner">
            {/* Navigation Arrows (show only if multiple promos) - positioned outside banner */}
            {promos.length > 1 && (
              <button
                onClick={() => setCurrentPromoIndex(prev => prev === 0 ? promos.length - 1 : prev - 1)}
                className="absolute -left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors border border-slate-200"
                data-testid="promo-prev-btn"
              >
                <ChevronLeft className="w-5 h-5 text-slate-700" />
              </button>
            )}
            
            {/* Slider Container with padding for arrows */}
            <div 
              ref={promoSliderRef}
              className={`overflow-hidden rounded-2xl ${promos.length > 1 ? 'mx-4' : ''}`}
            >
              <div 
                className="flex transition-transform duration-300 ease-out"
                style={{ transform: `translateX(-${currentPromoIndex * 100}%)` }}
              >
                {promos.map((promo, idx) => (
                  <div
                    key={promo.id || idx}
                    className="w-full flex-shrink-0"
                  >
                    <div className={`p-5 shadow-lg ${
                      idx % 3 === 0 ? 'bg-gradient-to-r from-amber-500 to-amber-400' :
                      idx % 3 === 1 ? 'bg-gradient-to-r from-emerald-500 to-teal-400' :
                      'bg-gradient-to-r from-purple-500 to-pink-400'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex-1 pr-4">
                          <p className="text-white/90 text-sm font-medium">{promo.description || 'Special Offer!'}</p>
                          <h2 className="text-white font-bold text-xl mt-1">
                            {promo.discount_type === 'percentage' 
                              ? `Get ${promo.discount_value}% OFF`
                              : `Save Php ${promo.discount_value}`}
                          </h2>
                          <p className="text-white text-sm mt-1 font-medium">Use code: <span className="font-bold bg-white/20 px-2 py-0.5 rounded">{promo.code}</span></p>
                          {promo.min_order_amount > 0 && (
                            <p className="text-white/80 text-xs mt-1">Min. order: Php {promo.min_order_amount}</p>
                          )}
                        </div>
                        <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                          <Star className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right Navigation Arrow */}
            {promos.length > 1 && (
              <button
                onClick={() => setCurrentPromoIndex(prev => prev === promos.length - 1 ? 0 : prev + 1)}
                className="absolute -right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-slate-50 transition-colors border border-slate-200"
                data-testid="promo-next-btn"
              >
                <ChevronRightIcon className="w-5 h-5 text-slate-700" />
              </button>
            )}
            
            {/* Dots Indicator (show only if multiple promos) */}
            {promos.length > 1 && (
              <div className="flex justify-center gap-2 mt-3">
                {promos.map((promo, idx) => (
                  <button
                    key={promo.id || `promo-${idx}`}
                    onClick={() => setCurrentPromoIndex(idx)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === currentPromoIndex ? 'bg-amber-500 w-4' : 'bg-slate-300'
                    }`}
                    data-testid={`promo-dot-${idx}`}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Categories */}
        <section className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900 font-[Poppins]">Services</h2>
            <button className="text-sm text-amber-600 font-medium" onClick={() => navigate('/service-search')}>View All</button>
          </div>
          
          {isLoading ? (
            <div className="grid grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={`skeleton-${i}`} className="animate-pulse">
                  <div className="w-full aspect-square bg-stone-200 rounded-2xl mb-2" />
                  <div className="h-3 bg-stone-200 rounded w-3/4 mx-auto" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-3">
              {categories.map((category) => {
                const IconComponent = iconMap[category.icon] || Wrench;
                // Use stored icon_color first, then fallback to colorMap
                const colorClass = category.icon_color 
                  ? (colorNameToClass[category.icon_color] || 'bg-slate-100 text-slate-600')
                  : (colorMap[category.name] || 'bg-slate-100 text-slate-600');
                
                return (
                  <button
                    key={category.id}
                    className="flex flex-col items-center"
                    onClick={() => navigate(`/service-search?category=${category.id}`)}
                    data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <div className={`w-14 h-14 ${colorClass} rounded-2xl flex items-center justify-center mb-2 hover:scale-105 transition-transform`}>
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <span className="text-xs text-slate-700 text-center leading-tight">{category.name}</span>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        {/* Quick Actions */}
        <section className="mb-6">
          <h2 className="text-lg font-bold text-slate-900 font-[Poppins] mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <button 
              className="bg-white border border-stone-100 rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow"
              onClick={() => navigate('/bookings')}
              data-testid="my-bookings-btn"
            >
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Calendar className="w-6 h-6 text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">My Bookings</p>
                <p className="text-xs text-slate-500">Track & manage</p>
              </div>
            </button>
            
            <button 
              className="bg-white border border-stone-100 rounded-2xl p-4 flex items-center gap-3 shadow-sm hover:shadow-md transition-shadow"
              onClick={() => navigate('/wallet')}
              data-testid="my-wallet-btn"
            >
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Wallet className="w-6 h-6 text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-semibold text-slate-900">My Wallet</p>
                <p className="text-xs text-slate-500">Balance & top-up</p>
              </div>
            </button>
          </div>
        </section>

        {/* Featured Providers Preview - Shows rating only, not personal details */}
        <section className="mb-24" data-testid="top-providers-section">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-900 font-[Poppins]">Top-Rated Heroes</h2>
            <button className="text-sm text-amber-600 font-medium" onClick={() => navigate('/service-search')}>Find Services</button>
          </div>
          
          {topProviders.length > 0 ? (
            <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2">
              {topProviders.map((provider, idx) => (
                <div 
                  key={provider.id}
                  className="flex-shrink-0 w-40 bg-white border border-stone-100 rounded-2xl p-4 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/service-search?provider_id=${provider.id}&provider_name=${encodeURIComponent(provider.name || provider.primary_service || 'Provider')}`)}
                  data-testid={`top-provider-${idx}`}
                >
                  <div className="w-16 h-16 rounded-full mx-auto mb-3 overflow-hidden bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
                    <Star className="w-8 h-8 text-amber-500" />
                  </div>
                  <h3 className="font-semibold text-slate-900 text-center text-sm truncate">{provider.primary_service || 'Service Provider'}</h3>
                  <p className="text-xs text-slate-500 text-center">Verified Provider</p>
                  <div className="flex items-center justify-center gap-1 mt-2">
                    <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
                    <span className="text-sm font-medium text-slate-900">{provider.rating?.toFixed(1) || '0.0'}</span>
                    <span className="text-xs text-slate-400">({provider.total_jobs || 0} jobs)</span>
                  </div>
                  <p className="text-xs text-blue-600 text-center mt-2">Tap to view services →</p>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-2xl p-6 text-center border border-stone-100">
              <Star className="w-12 h-12 text-amber-200 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Discover top-rated service providers in your area</p>
              <button 
                onClick={() => navigate('/service-search')}
                className="mt-3 px-4 py-2 bg-amber-500 text-slate-900 text-sm font-medium rounded-lg hover:bg-amber-600"
              >
                Search Services
              </button>
            </div>
          )}
        </section>
      </main>

      {/* Bottom Navigation */}
      <CustomerBottomNav />
    </div>
  );
}
